import React, { useMemo, useState } from "react";
import { State } from "country-state-city";

interface Location {
  city: string;
  state: string;
}

interface LocationFilterAccordionProps {
  locations: Location[];
  selected: string[];
  onToggle: (location: string) => void;
}

const getStateName = (stateCode: string): string =>
  State.getStateByCodeAndCountry(stateCode, "US")?.name ?? stateCode;

// Matches consecutive substrings first, then falls back to in-order character matching
// (e.g. "sndg" -> "San Diego") so typos/abbreviations still surface results.
const fuzzyMatch = (query: string, text: string): boolean => {
  if (!query) return true;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (t.includes(q)) return true;

  let qi = 0;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) qi++;
  }
  return qi === q.length;
};

const SearchIcon = () => (
  <svg
    className="size-4 shrink-0 text-gray-dark"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    aria-hidden="true"
  >
    <circle cx="9" cy="9" r="6" strokeWidth="1.5" />
    <path d="M17 17l-3.5-3.5" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ChevronIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <svg
    className={`size-4 shrink-0 text-blue-strong transition-transform ${
      isOpen ? "rotate-180" : ""
    }`}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" />
  </svg>
);

const LocationFilterAccordion: React.FC<LocationFilterAccordionProps> = ({
  locations,
  selected,
  onToggle,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedStates, setExpandedStates] = useState<
    Record<string, boolean>
  >({});

  const groupedStates = useMemo(() => {
    const byState = new Map<string, Set<string>>();
    locations.forEach(({ city, state }) => {
      if (!city || !state) return;
      if (!byState.has(state)) byState.set(state, new Set());
      byState.get(state)!.add(city);
    });

    return Array.from(byState.entries())
      .map(([state, cities]) => ({
        state,
        stateName: getStateName(state),
        cities: Array.from(cities).sort((a, b) => a.localeCompare(b)),
      }))
      .sort((a, b) => a.stateName.localeCompare(b.stateName));
  }, [locations]);

  const isSearching = searchTerm.trim().length > 0;

  const filteredStates = useMemo(() => {
    if (!isSearching) return groupedStates;

    return groupedStates
      .map((group) => ({
        ...group,
        cities: group.cities.filter(
          (city) =>
            fuzzyMatch(searchTerm, city) ||
            fuzzyMatch(searchTerm, group.stateName),
        ),
      }))
      .filter((group) => group.cities.length > 0);
  }, [groupedStates, searchTerm, isSearching]);

  const toggleState = (state: string) => {
    setExpandedStates((prev) => ({ ...prev, [state]: !prev[state] }));
  };

  return (
    <div className="flex w-full flex-col gap-2 bg-white p-2">
      <p className="text-lg font-semibold text-gray-dark">Location</p>
      <div className="flex min-h-[32px] w-full items-center gap-1.5 rounded-lg border-2 border-gray-light px-2 py-[5.5px]">
        <SearchIcon />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search"
          className="w-full flex-1 bg-transparent text-base text-gray-dark placeholder:text-gray-dark focus:outline-none"
        />
      </div>
      <div className="h-px w-full bg-blue-light/25" />
      <div className="flex w-full flex-col gap-1">
        {filteredStates.length === 0 && (
          <p className="py-2 text-sm text-gray-dark">No locations found.</p>
        )}
        {filteredStates.map((group) => {
          const isOpen = isSearching || !!expandedStates[group.state];
          return (
            <div
              key={group.state}
              className={
                isOpen ? "w-full border-b border-blue-light/25 pb-3" : "w-full"
              }
            >
              <button
                type="button"
                onClick={() => toggleState(group.state)}
                className="flex min-h-[32px] w-full items-center gap-2 rounded-md pr-2"
              >
                <span className="flex-1 text-left text-sm font-medium uppercase tracking-wide text-blue-strong">
                  {group.stateName}
                </span>
                <ChevronIcon isOpen={isOpen} />
              </button>
              {isOpen && (
                <div className="flex w-full flex-col gap-1">
                  {group.cities.map((city) => {
                    const value = `${city}, ${group.state}`;
                    return (
                      <label
                        key={value}
                        className="flex w-full cursor-pointer items-start gap-2 py-0.5"
                      >
                        <input
                          type="checkbox"
                          checked={selected.includes(value)}
                          onChange={() => onToggle(value)}
                          className="mt-0.5 size-4 shrink-0 rounded border-gray2"
                        />
                        <span className="flex-1 text-base leading-snug text-gray-dark">
                          {value}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LocationFilterAccordion;
